import {
    getPledgePayOpenCreditByGateway,
    getPledgePayOpenCreditPoolForGateway,
} from './pledge-pay-open-credit-context';
import { sumPledgeAmountPaidForGateway } from './pledge-pay-amount';
import { collectPledgePaymentRows } from './pledge-pay-rows';
import { formatCurrency } from './pledge-pay-utils';
import { animateAccountCreditApplied } from '../shared/account-credit-feedback';

function mgrPledgeBillGatewayCreditApplyInputFromUseCheckbox($useCb) {
    return $useCb.closest('.mgr-gateway-credit-panel').find('.mgr-gateway-credit-apply').first();
}

function soleOpenCreditGatewayIdFromPools() {
    const byGateway = getPledgePayOpenCreditByGateway();
    const keys = Object.keys(byGateway).filter((key) => {
        const gatewayId = parseInt(key, 10);
        return gatewayId > 0 && (parseFloat(byGateway[key]) || 0) > 0.005;
    });
    return keys.length === 1 ? String(keys[0]) : '';
}

function soleOpenCreditGatewayIdFromPledgeRows($modal) {
    const gatewayIds = new Set();
    collectPledgePaymentRows($modal).forEach((row) => {
        const gatewayId = parseInt(row.gateway_id, 10);
        if (gatewayId > 0) {
            gatewayIds.add(String(gatewayId));
        }
    });
    return gatewayIds.size === 1 ? [...gatewayIds][0] : '';
}

function mgrPledgeModalCollectGatewayCreditApply($modal) {
    const out = {};
    $modal.find('.mgr-gateway-use-credit:checked').each(function () {
        const $cb = $(this);
        const gid = String($cb.data('gateway-id') || '');
        if (!gid || gid === '0') {
            return;
        }
        const raw = String(mgrPledgeBillGatewayCreditApplyInputFromUseCheckbox($cb).val() || '').replace(/,/g, '').trim();
        const v = parseFloat(raw);
        if (Number.isFinite(v) && v > 0) {
            out[gid] = v.toFixed(2);
        }
    });
    if (Object.keys(out).length > 0) {
        return out;
    }

    const $cb = $modal.find('#mgr_use_open_payment_balance');
    const $inp = $modal.find('#mgr_open_payment_apply_amount');
    if (!$cb.length || !$cb.prop('checked') || !$inp.length) {
        return out;
    }
    const amount = parseFloat(String($inp.val()).replace(/,/g, '')) || 0;
    if (amount <= 0) {
        return out;
    }

    const gatewayId = soleOpenCreditGatewayIdFromPools() || soleOpenCreditGatewayIdFromPledgeRows($modal);
    if (!gatewayId) {
        return out;
    }

    out[gatewayId] = amount.toFixed(2);
    return out;
}

function mgrPledgeModalHasAnyGatewayCreditApplied($modal) {
    const byGateway = mgrPledgeModalCollectGatewayCreditApply($modal);
    return Object.keys(byGateway).some((k) => (parseFloat(byGateway[k]) || 0) > 0);
}

function mgrPledgeModalSyncGatewayCreditPanelDisplay($modal) {
    $modal.find('.mgr-gateway-credit-available').each(function () {
        const gid = String($(this).data('gateway-id') || '');
        const pool = getPledgePayOpenCreditPoolForGateway(gid);
        $(this).text(formatCurrency(pool));
        const $panel = $(this).closest('.mgr-gateway-credit-panel');
        const noCredit = pool <= 0.005;
        $panel.toggleClass('mgr-gateway-no-credit', noCredit);
        $panel.find('.mgr-gateway-credit-row').toggle(!noCredit);
        $panel.find('.mgr-gateway-no-credit-note').toggle(noCredit);
    });
}

function syncGatewayCreditApplication($root) {
    $root.find('.mgr-gateway-use-credit').each(function () {
        const $checkbox = $(this);
        const $panel = $checkbox.closest('.mgr-gateway-credit-panel');
        const $input = mgrPledgeBillGatewayCreditApplyInputFromUseCheckbox($checkbox);
        const gatewayId = String($checkbox.attr('data-gateway-id') || '');
        const pool = getPledgePayOpenCreditPoolForGateway(gatewayId);
        const amount = Math.max(0, Math.min(pool, sumPledgeAmountPaidForGateway($root, gatewayId)));
        const wasChecked = $checkbox.prop('checked');
        const mode = $checkbox.data('creditMode') || 'auto';
        $checkbox.prop('disabled', amount <= 0.005);
        if (mode === 'auto') {
            $checkbox.prop('checked', amount > 0.005);
            $input.val(amount > 0.005 ? amount.toFixed(2) : '');
        } else if (pool <= 0.005) {
            $checkbox.prop('checked', false);
            $input.val('');
        }
        const checked = $checkbox.prop('checked');
        $input.prop('disabled', !checked);
        $panel.toggleClass('mgr-gateway-credit-inactive', !checked);
        if (mode === 'auto' && checked && !wasChecked) {
            animateAccountCreditApplied($panel.find('.account-credit')[0]);
        }
    });
}

function syncOpenPaymentStatesGateway($modal, emptyPanelLayout) {
    emptyPanelLayout($modal, false);
    $modal.find('.mgr-gateway-credit-panel').show();
    syncGatewayCreditApplication($modal);
}

function recalcOpenApplyCapGateway($modal) {
    $modal.find('.mgr-gateway-use-credit:checked').each(function () {
        const $cb = $(this);
        const gid = String($cb.data('gateway-id') || '');
        const $inp = mgrPledgeBillGatewayCreditApplyInputFromUseCheckbox($cb);
        if (!$inp.length) {
            return;
        }
        const pool = getPledgePayOpenCreditPoolForGateway(gid);
        const groupPay = sumPledgeAmountPaidForGateway($modal, gid);
        const maxApply = Math.min(pool, groupPay);
        $inp.attr('data-mgr-apply-max', maxApply > 0 ? String(maxApply) : '0');
        $inp.removeAttr('max');
    });
}

function computeBalanceAppliedGateway($modal) {
    let balanceApplied = 0;
    $modal.find('.mgr-gateway-use-credit:checked').each(function () {
        const $cb = $(this);
        const gid = String($cb.data('gateway-id') || '');
        const raw = String(mgrPledgeBillGatewayCreditApplyInputFromUseCheckbox($cb).val() || '').replace(/,/g, '').trim();
        const applyRaw = parseFloat(raw) || 0;
        const pool = getPledgePayOpenCreditPoolForGateway(gid);
        const groupPay = sumPledgeAmountPaidForGateway($modal, gid);
        const maxApply = Math.min(pool, groupPay);
        balanceApplied += Math.min(Math.max(0, applyRaw), maxApply);
    });
    return Math.round(balanceApplied * 100) / 100;
}

export {
    mgrPledgeBillGatewayCreditApplyInputFromUseCheckbox,
    mgrPledgeModalCollectGatewayCreditApply,
    mgrPledgeModalHasAnyGatewayCreditApplied,
    mgrPledgeModalSyncGatewayCreditPanelDisplay,
    syncOpenPaymentStatesGateway,
    recalcOpenApplyCapGateway,
    computeBalanceAppliedGateway,
    syncGatewayCreditApplication,
};
